import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Home,
  Search,
  FileQuestion,
  Calculator,
  BookOpen,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import SEOHead from "../components/shared/SEOHead";

const POPULAR_LINKS = [
  { label: "App Marketing Services", to: "/", icon: Sparkles, desc: "Our 11 services for iOS & Android growth." },
  { label: "Free ASO Audit", to: "/free-aso-audit", icon: Search, desc: "30-second app marketing audit — no signup." },
  { label: "ROI Calculator", to: "/resources/roi-calculator", icon: Calculator, desc: "Project install uplift + payback from hiring us." },
  { label: "Case Studies", to: "/case-studies", icon: BookOpen, desc: "9 real client stories — Studdy, Porter, XM, more." },
  { label: "Discovery Questionnaire", to: "/discovery", icon: FileQuestion, desc: "Fill our 8-step brief before a sales call." },
  { label: "Pricing", to: "/pricing", icon: ArrowRight, desc: "Starter $299/mo + service-tier pricing." },
];

const TOP_SERVICES = [
  { label: "ASO", to: "/aso" },
  { label: "User Acquisition", to: "/user-acquisition" },
  { label: "Influencer Marketing", to: "/influencer-marketing" },
  { label: "App Retention", to: "/app-retention" },
  { label: "Creative Production", to: "/creative-production" },
  { label: "Analytics & Attribution", to: "/analytics" },
];

const TOP_VERTICALS = [
  { label: "Gaming", to: "/gaming-app-marketing" },
  { label: "FinTech", to: "/fintech-app-marketing" },
  { label: "EdTech", to: "/edtech-app-marketing" },
  { label: "Health", to: "/health-app-marketing" },
  { label: "E-commerce", to: "/ecommerce-app-marketing" },
];

export default function NotFoundPage() {
  const location = useLocation();
  const attempted = location.pathname;

  // Signal Navbar to use the dark-hero variant — works for arbitrary unmatched URLs.
  useEffect(() => {
    document.body.dataset.darkHero = "1";
    return () => {
      delete document.body.dataset.darkHero;
    };
  }, []);

  return (
    <>
      <SEOHead
        title="Page not found · 404 | AppFillip"
        description="The page you're looking for doesn't exist or has moved. Browse popular AppFillip resources, services and case studies."
        path="/404"
        noIndex
      />
      <Navbar />

      {/* Hero — brutalist 404 */}
      <section className="relative pt-28 pb-14 md:pt-32 md:pb-20 bg-charcoal text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern-dark opacity-30 pointer-events-none" />
        <motion.div
          className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-electric/20 blur-3xl pointer-events-none"
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden
        />

        <div className="container relative max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-1.5 mb-6 text-overline text-electric"
          >
            <FileQuestion className="w-3.5 h-3.5" /> Error 404 · Page not found
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-extrabold leading-[0.9] text-[5rem] sm:text-[7rem] md:text-[10rem] lg:text-[13rem] tracking-tight"
          >
            <span className="text-electric drop-shadow-[0_0_30px_rgba(33,150,243,0.4)]">4</span>
            <span className="text-white">0</span>
            <span className="text-electric drop-shadow-[0_0_30px_rgba(33,150,243,0.4)]">4</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-lg md:text-xl text-zinc-300 leading-relaxed max-w-2xl"
          >
            The page you're after doesn't exist, has moved, or was never on the
            map to begin with. No drama — let's find you something useful below.
          </motion.p>

          {attempted && attempted !== "/" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-4 inline-flex items-center gap-2 text-xs text-zinc-500 font-mono"
            >
              <span>Tried:</span>
              <code className="bg-white/5 border border-white/10 px-2 py-1 text-zinc-300 break-all">
                {attempted}
              </code>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 hover:-translate-y-0.5 transition-all shadow-[4px_4px_0_0_#fff]"
            >
              <Home className="w-4 h-4" /> Back to homepage
            </Link>
            <Link
              to="/free-aso-audit"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-white px-6 py-3.5 font-semibold transition-colors"
            >
              Get a free audit instead <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Popular destinations grid */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container max-w-5xl">
          <p className="text-overline text-zinc-500 mb-2">Popular destinations</p>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal mb-8">
            Where most visitors actually go
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {POPULAR_LINKS.map((l) => {
              const Icon = l.icon;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className="group p-5 border-2 border-zinc-200 bg-white hover:border-electric hover:-translate-y-0.5 transition-all shadow-[3px_3px_0_0_transparent] hover:shadow-[3px_3px_0_0_#2196F3]"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-charcoal text-electric group-hover:bg-electric group-hover:text-white transition-colors">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-extrabold text-charcoal group-hover:text-electric transition-colors">
                        {l.label}
                      </p>
                      <p className="text-sm text-zinc-600 leading-relaxed mt-1">
                        {l.desc}
                      </p>
                      <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-electric">
                        Open <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick links — services + verticals */}
      <section className="py-14 bg-zinc-50 border-y border-zinc-200">
        <div className="container max-w-5xl grid md:grid-cols-2 gap-10">
          <div>
            <p className="text-overline text-zinc-500 mb-3">Top services</p>
            <ul className="space-y-2">
              {TOP_SERVICES.map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    className="inline-flex items-center gap-2 text-charcoal font-bold hover:text-electric transition-colors"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-electric" /> {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-overline text-zinc-500 mb-3">Industry playbooks</p>
            <ul className="space-y-2">
              {TOP_VERTICALS.map((v) => (
                <li key={v.to}>
                  <Link
                    to={v.to}
                    className="inline-flex items-center gap-2 text-charcoal font-bold hover:text-electric transition-colors"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-electric" /> {v.label} app marketing
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-charcoal text-white">
        <div className="container max-w-3xl text-center">
          <h3 className="font-display text-2xl md:text-3xl font-extrabold leading-tight">
            Still can't find what you need?
          </h3>
          <p className="mt-3 text-zinc-300 leading-relaxed">
            Tell us what you were looking for — we'll point you to the right
            resource (or build it if it should exist).
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-electric text-white px-5 py-3 font-bold text-sm hover:bg-electric-400 transition-colors shadow-[3px_3px_0_0_#fff]"
            >
              Contact AppFillip <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://wa.me/919950510511"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-white px-5 py-3 font-bold text-sm transition-colors"
            >
              WhatsApp us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
